import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { Link } from '@tanstack/react-router';
import { BotonDeAnulacion } from '../componentes/ui/anulacion';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { DialogoDeCobranza } from './DialogoDeCobranza';

interface TramosDeAntiguedad {
  readonly terceroId: string;
  readonly alDia: string;
  readonly hasta30: string;
  readonly hasta60: string;
  readonly hasta90: string;
  readonly masDe90: string;
}

interface CuentaConSaldo {
  readonly terceroId: string;
  readonly razonSocial: string;
  readonly identificacion: string | null;
  readonly saldo: string;
  readonly limiteDeCredito: string | null;
  readonly diasDePago: number | null;
}

interface Movimiento {
  readonly id: string;
  readonly tipo: string;
  readonly importe: string;
  readonly vencimiento: string | null;
  readonly concepto: string | null;
  readonly ocurridoEn: string;
  readonly referenciaTipo: string | null;
  readonly referenciaId: string | null;
}

function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * Cuentas corrientes: quién debe y cuánto.
 *
 * Abre por saldo de mayor a menor, que es el orden en que se sale a cobrar. La ficha de cada uno
 * muestra el libro entero: de dónde salió cada peso que debe.
 *
 * El ajuste está acá y no escondido en una pantalla de administración porque un saldo que no se
 * puede corregir obliga al comercio a inventarse una venta falsa, que ensucia mucho más.
 */
export function Cuentas(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeAjustar = usePermiso('CUENTAS.AJUSTE.APLICAR');
  const puedeCobrar = usePermiso('CUENTAS.COBRO.REGISTRAR');
  const puedeAnularCobros = usePermiso('CUENTAS.COBRO.ANULAR');

  const [abierta, setAbierta] = useState<CuentaConSaldo | null>(null);
  const [ajustando, setAjustando] = useState<CuentaConSaldo | null>(null);
  const [cobrando, setCobrando] = useState<CuentaConSaldo | null>(null);
  const [importe, setImporte] = useState('');
  const [concepto, setConcepto] = useState('');
  const [error, setError] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['cuentas-corrientes'],
    queryFn: () => pedir<CuentaConSaldo[]>('/cuentas-corrientes'),
  });

  // La antigüedad viene aparte y no dentro del saldo: son dos preguntas distintas —cuánto debe y
  // desde cuándo— y quien mira la primera no siempre necesita la segunda.
  const antiguedad = useQuery({
    queryKey: ['antiguedad-de-clientes'],
    queryFn: () => pedir<TramosDeAntiguedad[]>('/cuentas-corrientes/antiguedad'),
  });

  const tramosDe = (terceroId: string): TramosDeAntiguedad | undefined =>
    antiguedad.data?.find((fila) => fila.terceroId === terceroId);

  /**
   * Lo que el cliente adelantó y todavía no se aplicó.
   *
   * Va en la ficha y no en una pantalla aparte porque **es parte de la respuesta a «cuánto me
   * debe»**: un saldo de 5.000 con 50.000 a cuenta no es lo mismo que un saldo de 5.000 pelado, y
   * quien sale a cobrar necesita saberlo antes de llamar.
   */
  const aCuenta = useQuery({
    queryKey: ['a-cuenta', abierta?.terceroId],
    enabled: abierta !== null,
    queryFn: () =>
      pedir<{ reciboId: string; numero: string; fecha: string; sinAplicar: string }[]>(
        `/cuentas-corrientes/${abierta?.terceroId ?? ''}/a-cuenta`,
      ),
  });

  const aplicar = useMutation({
    mutationFn: (datos: { reciboId: string; movimientoId: string; importe: string }) =>
      pedir<{ aplicado: string }>(`/cuentas-corrientes/recibos/${datos.reciboId}/imputaciones`, {
        method: 'POST',
        cuerpo: {
          imputaciones: [{ movimientoId: datos.movimientoId, importe: datos.importe }],
        },
      }),
    onSuccess: async () => {
      for (const clave of ['a-cuenta', 'cuenta-corriente', 'cuentas-corrientes', 'pendientes']) {
        await clienteDeConsultas.invalidateQueries({ queryKey: [clave] });
      }
    },
  });

  const ficha = useQuery({
    queryKey: ['cuenta-corriente', abierta?.terceroId],
    enabled: abierta !== null,
    queryFn: () =>
      pedir<{ saldo: string; movimientos: Movimiento[] }>(
        `/cuentas-corrientes/${abierta?.terceroId ?? ''}`,
      ),
  });

  const ajustar = useMutation({
    mutationFn: () =>
      pedir<{ saldo: string }>('/cuentas-corrientes/ajustes', {
        method: 'POST',
        cuerpo: {
          terceroId: ajustando?.terceroId ?? '',
          importe: importe.trim().replace(',', '.'),
          concepto: concepto.trim(),
        },
      }),
    onSuccess: async () => {
      setAjustando(null);
      setImporte('');
      setConcepto('');
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-corrientes'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cuenta-corriente'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const columnas: Columna<CuentaConSaldo>[] = [
    { clave: 'razonSocial', titulo: t('cuentas.cliente'), contenido: (fila) => fila.razonSocial },
    {
      clave: 'identificacion',
      titulo: t('cuentas.identificacion'),
      contenido: (fila) => <span className="numero">{fila.identificacion ?? '—'}</span>,
    },
    {
      clave: 'saldo',
      titulo: t('cuentas.saldo'),
      numerica: true,
      // Un saldo negativo no es un error: es plata a favor del cliente, y se dice con palabras
      // para que nadie lo lea como una deuda con signo raro.
      contenido: (fila) =>
        Number(fila.saldo) < 0 ? (
          <Insignia tono="bien">
            {t('cuentas.aFavor', { importe: conDosDecimales(fila.saldo.replace('-', '')) })}
          </Insignia>
        ) : (
          <span className="numero">{conDosDecimales(fila.saldo)}</span>
        ),
    },
    {
      clave: 'antiguedad',
      titulo: t('cuentas.antiguedad'),
      // Lo vencido primero y con más peso: es lo que decide a quién se llama hoy.
      contenido: (fila) => {
        const tramos = tramosDe(fila.terceroId);
        if (tramos === undefined) return <span className="text-texto-tenue">—</span>;
        const vencido =
          Number(tramos.hasta30) +
          Number(tramos.hasta60) +
          Number(tramos.hasta90) +
          Number(tramos.masDe90);
        return vencido === 0 ? (
          <Insignia tono="bien">{t('cuentas.alDia')}</Insignia>
        ) : (
          <span className="text-xs">
            <span className="numero block">{conDosDecimales(String(vencido))}</span>
            <span className="text-texto-tenue">
              {Number(tramos.masDe90) > 0 ? t('cuentas.masDe90') : t('cuentas.porTramos')}
            </span>
          </span>
        );
      },
    },
    {
      clave: 'limite',
      titulo: t('cuentas.limite'),
      numerica: true,
      contenido: (fila) =>
        fila.limiteDeCredito === null ? (
          <span className="text-texto-tenue">{t('cuentas.sinLimite')}</span>
        ) : (
          <span className="numero">{conDosDecimales(fila.limiteDeCredito)}</span>
        ),
    },
    {
      clave: 'acciones',
      titulo: t('cuentas.acciones'),
      contenido: (fila) => (
        <div className="flex gap-2">
          {puedeCobrar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setCobrando(fila);
              }}
            >
              {t('cuentas.cobrar')}
            </Boton>
          ) : null}
          <Boton
            tono="secundario"
            onClick={() => {
              setAbierta(fila);
            }}
          >
            {t('cuentas.verFicha')}
          </Boton>
          <Link
            to="/estado-de-cuenta/$terceroId"
            params={{ terceroId: fila.terceroId }}
            className="rounded-marco border border-borde px-3 py-1.5 text-sm hover:bg-acento-suave"
          >
            {t('cuentas.verEstado')}
          </Link>
          {puedeAjustar ? (
            <Boton
              tono="secundario"
              onClick={() => {
                setAjustando(fila);
                setError(null);
              }}
            >
              {t('cuentas.ajustar')}
            </Boton>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <Encabezado titulo={t('cuentas.titulo')} descripcion={t('cuentas.descripcion')} />

      <Tarjeta>
        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => fila.terceroId}
            vacio={<Vacio descripcion={t('cuentas.nadieDebe')} />}
          />
        ) : null}
      </Tarjeta>

      <DialogoDeCobranza
        terceroId={cobrando?.terceroId ?? null}
        nombre={cobrando?.razonSocial ?? ''}
        abierto={cobrando !== null}
        onCerrar={() => {
          setCobrando(null);
        }}
      />

      <Dialogo
        abierto={abierta !== null}
        titulo={abierta?.razonSocial ?? ''}
        onCerrar={() => {
          setAbierta(null);
        }}
      >
        {ficha.isPending ? <Cargando /> : null}
        {ficha.isSuccess ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm">
              {t('cuentas.saldo')}:{' '}
              <span className="numero font-semibold">{conDosDecimales(ficha.data.saldo)}</span>
            </p>
            {/*
              Lo adelantado y sin aplicar. Va **arriba de los movimientos** porque cambia cómo se
              lee todo lo de abajo: una factura pendiente con plata a cuenta del mismo cliente no
              es una deuda, es una imputación que falta hacer.
            */}
            {(aCuenta.data ?? []).length === 0 ? null : (
              <div className="rounded-marco bg-bien-suave p-3">
                <p className="text-apoyo font-semibold text-bien">{t('cuentas.aCuenta')}</p>
                <ul className="mt-1 flex flex-col gap-1">
                  {(aCuenta.data ?? []).map((adelanto) => (
                    <li key={adelanto.reciboId} className="flex items-center gap-2 text-cuerpo">
                      <span className="min-w-0 flex-1">
                        {t('cuentas.recibo', { numero: adelanto.numero })}
                        <span className="block text-apoyo text-texto-tenue">{adelanto.fecha}</span>
                      </span>
                      <span className="cifra">{conDosDecimales(adelanto.sinAplicar)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <ul className="flex flex-col divide-y divide-borde text-sm">
              {ficha.data.movimientos.map((movimiento) => (
                <li key={movimiento.id} className="flex items-center justify-between gap-3 py-2">
                  <span>
                    <span className="block text-xs text-texto-tenue">
                      {new Date(movimiento.ocurridoEn).toLocaleDateString()} ·{' '}
                      {t(`cuentas.tipos.${movimiento.tipo}`, movimiento.tipo)}
                    </span>
                    {movimiento.concepto ?? ''}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="numero">{conDosDecimales(movimiento.importe)}</span>
                    {/* Aplicar lo adelantado a este comprobante. Aparece sólo si hay algo que
                        aplicar y este movimiento es una deuda: ofrecerlo siempre obligaría a
                        entender por qué no hace nada. */}
                    {(aCuenta.data ?? []).length > 0 && Number(movimiento.importe) > 0 ? (
                      <Boton
                        cargando={aplicar.isPending}
                        onClick={() => {
                          const adelanto = (aCuenta.data ?? [])[0];
                          if (adelanto === undefined) return;
                          const cuanto =
                            Number(adelanto.sinAplicar) < Number(movimiento.importe)
                              ? adelanto.sinAplicar
                              : movimiento.importe;
                          aplicar.mutate({
                            reciboId: adelanto.reciboId,
                            movimientoId: movimiento.id,
                            importe: cuanto,
                          });
                        }}
                      >
                        {t('cuentas.aplicar')}
                      </Boton>
                    ) : null}
                    {/* Se anula desde el estado de cuenta, que es donde se nota que un cobro
                        está mal: mirando la cuenta del cliente, no una lista de recibos. */}
                    {puedeAnularCobros && movimiento.referenciaTipo === 'recibo_de_cobranza' ? (
                      <BotonDeAnulacion
                        ruta={`/cuentas-corrientes/recibos/${movimiento.referenciaId ?? ''}/anulacion`}
                        onAnulado={async () => {
                          await clienteDeConsultas.invalidateQueries({
                            queryKey: ['cuenta-corriente'],
                          });
                          await clienteDeConsultas.invalidateQueries({
                            queryKey: ['cuentas-corrientes'],
                          });
                        }}
                      />
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Dialogo>

      <Dialogo
        abierto={ajustando !== null}
        titulo={t('cuentas.ajustarSaldo')}
        onCerrar={() => {
          setAjustando(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={ajustar.isPending}
            disabled={importe.trim() === '' || concepto.trim() === ''}
            onClick={() => {
              ajustar.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('cuentas.importeDelAjuste')}
            inputMode="decimal"
            value={importe}
            ayuda={t('cuentas.ayudaImporteDelAjuste')}
            onChange={(evento) => {
              setImporte(evento.target.value);
            }}
          />
          <Campo
            etiqueta={t('cuentas.motivo')}
            obligatorio
            maxLength={200}
            value={concepto}
            ayuda={t('cuentas.ayudaMotivo')}
            onChange={(evento) => {
              setConcepto(evento.target.value);
            }}
          />
          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
